<template>
    <e-collapse
        v-model:open="isOpen"
        v-observe-visibility="visibilityChanged"
        class="card tw:min-w-0 tw:max-w-full tw:overflow-x-clip"
        :class="{ 'is-important': announcement.important }"
    >
        <template #trigger>
            <header class="card-header tw:w-full tw:min-w-0 tw:max-w-full tw:md:px-6 tw:md:py-5" :style="isNotDesktop ? 'display: block' : ''">
                <div class="card-header-title tw:w-full! tw:min-w-0 tw:max-w-full" :class="{ 'desktop-header': !isNotDesktop }">
                    <div class="tw:flex tw:flex-col tw:w-full! tw:min-w-0 tw:max-w-full">
                        <div class="tw:flex tw:w-full tw:min-w-0 tw:max-w-full">
                            <div class="announcement-header">
                                <div class="announcement-header__content">
                                    <div class="announcement-header__identity">
                                        <div class="tw:flex tw:shrink-0 tw:md:gap-2" :class="{ 'tw:items-center': !isNotDesktop }">
                                            <div class="avatar-container">
                                                <div v-if="!markedAsRead" class="unread" />
                                                <user-avatar :img-url="get(announcer, 'photo_url')" :title="get(announcer, 'name')" />
                                            </div>
                                        </div>

                                        <p v-if="!isNotDesktop" class="announcement-header__title">{{ announcement.title }}</p>
                                    </div>

                                    <div v-if="isNotDesktop" class="announcement-header__mobile-meta">
                                        <div class="announcement-header__mobile-chips">
                                            <e-tag
                                                v-if="announcement.important"
                                                class="tag is-danger announcement-header__mobile-tag tw:max-w-full tw:h-auto tw:whitespace-normal!"
                                                icon="exclamation-circle"
                                            >
                                                Important
                                            </e-tag>
                                            <p v-if="!isStudent" class="announcement-header__mobile-created tw:min-w-0 tw:wrap-anywhere">
                                                Created:
                                                <date-format format="LLL zz" :date="announcement.updated_at" />
                                            </p>
                                        </div>
                                        <div v-if="!isStudent && isFutureAnnouncement(announcement)" class="has-text-primary">
                                            <e-tooltip
                                                label="Announcement is not published. It will be visible to students at published date."
                                                position="is-right"
                                                multilined
                                                :active="active"
                                            >
                                                <e-icon
                                                    size="is-small"
                                                    style="padding-bottom: -7rem; display: flex"
                                                    icon="calendar-alt"
                                                />
                                                <i class="stack-icon uil uil-clock" />
                                            </e-tooltip>
                                        </div>
                                    </div>

                                    <template v-if="!isNotDesktop">
                                        <e-tag v-show="announcement.important" class="announcement-header__tag tw:text-sm tw:font-normal!" icon="exclamation-circle" type="is-danger">
                                            Important
                                        </e-tag>
                                        <e-tag v-if="!isStudent" class="announcement-header__tag tw:text-sm tw:font-normal!">
                                            Created:
                                            <date-format format="LLL zz" :date="announcement.updated_at" />
                                        </e-tag>
                                        <e-tag class="announcement-header__tag tw:text-sm tw:font-normal!">
                                            {{ isStudent ? '' : 'Publish Date:' }}
                                            <date-format format="LLL zz" :date="announcement.published_at" />
                                        </e-tag>
                                        <div v-if="!isStudent && isFutureAnnouncement(announcement)" class="has-text-primary">
                                            <e-tooltip
                                                label="Announcement is not published. It will be visible to students at published date."
                                                position="is-right"
                                                multilined
                                                :active="active"
                                            >
                                                <e-icon
                                                    size="is-small"
                                                    style="padding-bottom: -7rem; display: flex"
                                                    icon="calendar-alt"
                                                />
                                                <i class="stack-icon uil uil-clock" />
                                            </e-tooltip>
                                        </div>
                                    </template>
                                </div>
                                <div v-if="($gate.allows('update', announcement) || $gate.allows('delete', announcement))" class="announcement-header__actions">
                                    <ButtonGroup>
                                        <e-button
                                            v-if="$gate.allows('update', announcement) && type === 'Course'"
                                            type="secondary"
                                            class="tw:bg-transparent"
                                            @click.stop="openEditModal"
                                            @keyup.enter.prevent.stop="openEditModal"
                                            @keyup.space.prevent.stop="openEditModal"
                                        >
                                            <e-icon icon="pen" />
                                        </e-button>
                                        <e-button
                                            v-if="$gate.allows('delete', announcement)"
                                            type="secondary"
                                            class="tw:bg-transparent tw:text-red-500"
                                            :loading="deleting"
                                            @click="deletePost"
                                            @keyup="deletePost"
                                        >
                                            <e-icon icon="trash-alt" />
                                        </e-button>
                                    </ButtonGroup>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-show="isNotDesktop" class="p-l-md p-r-md">
                    <div>
                        <p class="tw:font-semibold">{{ announcement.title }}</p>

                        <p v-if="!isOpen" class="has-text-weight-normal" v-html="previewBodyHtml" />
                    </div>
                </div>
            </header>
        </template>

        <div class="card-content">
            <display-content v-if="announcement.body" :content="announcement.body" />

            <attachments v-if="!isEmpty(announcement.media)" :media="announcement.media" />
            <e-tag v-if="isNotDesktop" class="announcement-header__publish tw:max-w-full tw:h-auto tw:font-semibold tw:whitespace-normal!">
                {{ isStudent ? '' : 'Publish Date:' }}
                <date-format format="LLL zz" :date="announcement.published_at" />
            </e-tag>
            <p v-if="!announcement.body" class="has-text-subtle">There is no message to display.</p>
        </div>
    </e-collapse>

    <e-modal
        v-model="isEditModalActive"
        :width="isNotDesktop ? undefined : 900"
        has-modal-card
        trap-focus
        can-cancel
        aria-role="dialog"
        aria-modal
        class="announcement-edit-modal"
        @close="handleModalClose"
    >
        <edit-announcement-modal
            :announcement-id="announcement.id"
            :announcement-title="announcement.title || ''"
            :announcement-body="announcement.body || ''"
            :announcement-published-at="announcement.published_at || null"
            :announcement-send-notification="announcement.send_notification || false"
            :important="editImportant"
            :type="type"
            :saving="updating"
            @save="updateAnnouncement"
            @close="handleModalClose"
        />
    </e-modal>
</template>

<script>
import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';
import { mapState } from 'vuex';

import Attachments from '@/components/activity-types/course-activity-types/activities/Attachments.vue';
import EditAnnouncementModal from '@/components/announcements/EditAnnouncementModal.vue';
import DateFormat from '@/components/date-format/DateFormat.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import Avatar from '@/components/elements/Avatar.vue';
import { handleResponseError } from '@/Helpers.js';
import MarkAsReadMixin from '@/mixins/MarkAsRead.mixin.js';
import ActivityUpdate from '@/models/ActivityUpdate.js';
import CourseAnnouncement from '@/models/CourseAnnouncement.js';
import { stripInlineTextColors } from '@/utilities/stripInlineTextColors.js';

export default {
    name: 'AnnouncementCard',

    components: { Attachments, UserAvatar: Avatar, DisplayContent, DateFormat, EditAnnouncementModal },

    mixins: [MarkAsReadMixin],

    props: {
        announcement: {
            default: null,
            type: Object,
        },

        open: {
            type: Boolean,
            required: false,
            default: false,
        },

        type: {
            required: true,
            type: String,
        },

        course: {
            type: Object,
            required: true,
        },
    },

    emits: ['edit-modal-open', 'edit-modal-close'],

    data: () => ({
        isOpen: false,
        deleting: false,
        active: true,
        isEditModalActive: false,
        editImportant: false,
        updating: false,
    }),

    computed: {
        ...mapState('Auth', { user: state => state.user }),

        isStudent() {
            return this.user.isStudentOf(this.course.id);
        },

        announcer() {
            return this.announcement.creator;
        },

        isNotDesktop() {
            return this.$isMobile || this.$isTablet;
        },

        previewBodyHtml() {
            return this.truncate(stripInlineTextColors(this.announcement.body), 10);
        },
    },

    created() {
        this.markAsReadConfig.timeout = null;
        this.markAsReadConfig.model = this.announcement;
        this.markAsReadConfig.modelType = 'App\\Models\\CourseAnnouncement';
        this.markAsReadConfig.identifierField = 'created_by';

        this.isOpen = this.open;
    },

    methods: {
        get,
        isEmpty,

        deletePost() {
            this.$dialog.confirm({
                title: 'Delete Post?',
                message: 'Are you sure you want to <b>delete</b> this announcement? This action cannot be undone.',
                confirmText: 'Delete Post',
                type: 'is-danger',
                onConfirm: async () => {
                    this.deleting = true;

                    try {
                        await this.$store.dispatch(`${this.type}Announcements/deleteAnnouncement`, this.announcement);

                        this.announcement.$delete();

                        this.$edverumToast.open('Post deleted!');
                    } catch (e) {
                        handleResponseError(e);
                    }

                    this.deleting = false;
                },
            });
        },

        openEditModal(event) {
            if (event) {
                event.stopPropagation();
                event.preventDefault();
            }
            this.editImportant = this.announcement.important || false;
            this.$nextTick(() => {
                this.isEditModalActive = true;
                this.$emit('edit-modal-open');
            });
        },

        async updateAnnouncement(important) {
            this.updating = true;

            try {
                const updatedData = await this.$store.dispatch(`${this.type}Announcements/updateAnnouncement`, {
                    announcement: this.announcement,
                    important,
                });

                // Update the model in Vuex ORM to ensure reactivity
                const ModelClass = this.type === 'Activity' ? ActivityUpdate : CourseAnnouncement;
                ModelClass.insertOrUpdate({ data: updatedData });

                // Wait for the next tick to ensure the computed property in the parent has re-evaluated
                await this.$nextTick();

                this.$edverumToast.open({
                    message: 'Announcement updated successfully!',
                    type: 'is-success',
                });

                // Close modal only after successful update
                this.closeEditModal();
            } catch (e) {
                handleResponseError(e);
                // Keep modal open on error so user can try again
            } finally {
                this.updating = false;
            }
        },

        isFutureAnnouncement(announcement) {
            return this.$moment.utc(announcement.published_at).tz(this.$timezone.name).isAfter(new Date());
        },

        closeEditModal() {
            if (!this.isEditModalActive) {
                return;
            }

            this.isEditModalActive = false;
            this.$emit('edit-modal-close');
        },

        handleModalClose() {
            if (!this.updating) {
                this.closeEditModal();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

.desktop-header {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    padding: 0;
}

.announcement-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    gap: 0.75rem;
    width: 100%;
    min-width: 0;
}

.announcement-header__content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    min-width: 0;
}

.announcement-header__identity {
    display: flex;
    align-items: center;
    gap: 1rem;
    min-width: 0;
    max-width: 100%;
}

.announcement-header__title {
    min-width: 0;
    overflow-wrap: break-word;
    font-size: 1rem;
    font-weight: 600;
}

.announcement-header__tag {
    flex: 0 0 auto;
    white-space: nowrap;
}

.announcement-header__actions {
    flex-shrink: 0;
}

/* Mobile and tablet (under 1200px) reuse this header. The Important chip and
   Created line must shrink and wrap; a nowrap row was widening .layout-content
   by about 11px at a 375px viewport. */
.announcement-header__mobile-meta {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 0.25rem;
    max-width: 100%;
    min-width: 0;
}

.announcement-header__mobile-chips {
    max-width: 100%;
    min-width: 0;
}

.announcement-header__mobile-created {
    min-width: 0;
    overflow-wrap: anywhere;
}

.announcement-header__mobile-tag,
.announcement-header__publish {
    height: auto;
    max-width: 100%;
    white-space: normal;
}

.stack-icon {
    margin-left: 0.5rem;
    margin-top: -1.7rem;
    display: flex;
}

.avatar-container {
    position: relative;
}

.unread {
    position: absolute;
    top: -0.5rem;
    left: -0.5rem;
    width: 1rem;
    height: 1rem;
    background: $primary;
    border-radius: 50%;
    z-index: 10;
}

.is-important {
    border-left: 7px solid #fc6c6c !important;
}

/* Responsive modal styling */
:deep(.announcement-edit-modal) {
    .modal-card {
        @media screen and (max-width: 768px) {
            width: calc(100vw - 20px) !important;
            max-width: 100% !important;
            margin: 0 auto;
        }

        @media screen and (min-width: 769px) {
            width: 900px;
        }
    }
}
</style>
